import {StyleSheet} from "aphrodite";

import {DetailCell} from "@khanacademy/wonder-blocks-cell";
import Clickable from "@khanacademy/wonder-blocks-clickable";
import {View} from "@khanacademy/wonder-blocks-core";
import {
    PhosphorIcon,
    type PhosphorIconAsset,
} from "@khanacademy/wonder-blocks-icon";
import IconButton from "@khanacademy/wonder-blocks-icon-button";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {
    HeadingXSmall,
    LabelLarge,
    LabelSmall,
} from "@khanacademy/wonder-blocks-typography";
import {formatStandards, type MappedStandard} from "@khan/guide-core";

import xIcon from "@phosphor-icons/core/regular/x.svg";

interface Props {
    title: string;
    chosen?: boolean;
    disabled?: boolean;
    contentDescriptor: string;
    courseTitle: string;
    description: string;
    icon?: PhosphorIconAsset;
    mappedStandards?: MappedStandard[];
    onClick: (contentDescriptor: string) => void;
}

export default function ClickableContentCell(props: Props) {
    const {
        chosen,
        disabled,
        contentDescriptor,
        title,
        description,
        mappedStandards,
        courseTitle,
        icon,
        onClick,
    } = props;

    let subtitle = (
        <LabelSmall style={styles.description}>{description}</LabelSmall>
    );
    if (mappedStandards?.length) {
        subtitle = (
            <View>
                {subtitle}
                <Strut size={spacing.xxxxSmall_2} />
                <LabelSmall style={styles.standards}>
                    {formatStandards(mappedStandards)}
                </LabelSmall>
            </View>
        );
    }

    const renderCell = (hovered: boolean) => (
        <DetailCell
            style={[
                hovered && !chosen && styles.cellHovered,
                chosen && styles.cellChosen,
                !contentDescriptor && styles.finalCell,
            ]}
            title={
                <LabelLarge
                    style={[
                        styles.title,
                        hovered && !chosen && styles.titleHovered,
                    ]}
                >
                    {title}
                </LabelLarge>
            }
            subtitle1={
                contentDescriptor ? (
                    <View
                        style={[
                            styles.subtitle,
                            hovered && !chosen && styles.titleHovered,
                        ]}
                    >
                        <HeadingXSmall>{courseTitle}</HeadingXSmall>
                    </View>
                ) : undefined
            }
            subtitle2={subtitle}
            leftAccessory={
                icon ? (
                    <PhosphorIcon
                        icon={icon}
                        size="medium"
                        color={
                            hovered && !chosen ? color.white : color.offBlack64
                        }
                    />
                ) : null
            }
            rightAccessory={
                chosen && !disabled ? (
                    <IconButton
                        icon={xIcon}
                        size="medium"
                        kind="tertiary"
                        actionType="neutral"
                        onClick={() => onClick("")}
                    />
                ) : null
            }
        />
    );

    if (chosen) {
        return renderCell(false);
    }

    return (
        <Clickable onClick={() => onClick(contentDescriptor)}>
            {({hovered}) => renderCell(hovered)}
        </Clickable>
    );
}

const styles = StyleSheet.create({
    finalCell: {
        borderEndStartRadius: spacing.xxxSmall_4,
        borderEndEndRadius: spacing.xxxSmall_4,
    },
    cellChosen: {
        borderRadius: spacing.xxxSmall_4,
        border: `1px solid ${color.offBlack16}`,
    },
    cellHovered: {
        background: color.blue,
        color: color.white,
    },
    title: {
        color: color.blue,
    },
    titleHovered: {
        color: color.white,
    },
    subtitle: {
        flexDirection: "row",
        color: color.offBlack64,
    },
    description: {
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
    },
    standards: {
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
    },
});
